import { useState, useEffect } from 'react';
import useFileDownload from './useFileDownload';
import logger from '../utils/logger';

const thumbnailBlobUrls = new WeakMap();

/**
 * Hook to load a media thumbnail from a Matrix content URL (mxc://).
 * It uses the downloadFileToDisplay function which handles caching.
 *
 * @param {object} message - The Matrix event object.
 * @param {string|null} mxcUrl - The mxc:// URL of the thumbnail.
 * @returns {{thumbnailUrl: string|null, isLoading: boolean, error: string|null}}
 */
export const useMediaThumbnail = (message, mxcUrl) => {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { downloadFileToDisplay } = useFileDownload();

  // Extract stable values from message
  const messageId = message?.getId();
  const roomId = message?.getRoomId();

  useEffect(() => {
    let isMounted = true;
    const blobUrlRef = { current: null };

    const loadThumbnail = async () => {
      if (!mxcUrl) {
        setThumbnailUrl(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const url = await downloadFileToDisplay({ url: mxcUrl, roomId });
        blobUrlRef.current = url; // Store in ref for cleanup access

        if (isMounted) {
          setThumbnailUrl(url);
          if (message) {
            thumbnailBlobUrls.set(message, url);
          }
        } else {
          // Component unmounted during fetch - revoke immediately
          URL.revokeObjectURL(url);
        }
      } catch (e) {
        logger.error('Failed to load thumbnail:', e);
        if (isMounted) {
          setError('Failed to load thumbnail');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadThumbnail();

    return () => {
      isMounted = false;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        // Remove from WeakMap to prevent other components from retrieving revoked URL
        if (message) {
          thumbnailBlobUrls.delete(message);
        }
      }
    };
  }, [mxcUrl, downloadFileToDisplay, messageId, roomId]);

  return { thumbnailUrl, isLoading, error };
};

export const getThumbnailBlobUrl = (message) => {
  return thumbnailBlobUrls.get(message);
};